import React, { useState } from 'react'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Stack,
  Grid,
  CircularProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Divider,
} from '@mui/material'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import * as XLSX from 'xlsx'
import { payoutService } from '../services/payoutService'
import { useNavigate } from 'react-router-dom'

const TEMPLATE_HEADERS = [
  'riderPublicID',
  'clientName',
  'beneficiaryName',
  'beneficiaryAccountNo',
  'ifsc',
  'transactionType',
  'debitAccountNo',
  'transactionDate',
  'amount',
  'remarks',
  'utrNumber',
  'status',
  'periodStartDate',
  'periodEndDate',
]

const sampleRow = [
  'RIDER123',
  'Client A',
  'Beneficiary X',
  '1234567890',
  'IFSC0001',
  'NEFT',
  '000111222333',
  '2025-12-30T10:00:00Z',
  '1000',
  'Optional remarks',
  'UTR123456',
  'SUCCESS',
  '2025-12-01',
  '2025-12-15',
]

const PayoutsBulkUpload: React.FC = () => {
  const navigate = useNavigate()
  const [file, setFile] = useState<File | null>(null)
  const [uploading, setUploading] = useState(false)
  const [result, setResult] = useState<any>(null)
  const [error, setError] = useState<string | null>(null)

  const downloadTemplate = () => {
    // Generate a real Excel .xlsx template using SheetJS so uploads won't fail parsing
    const wsData = [TEMPLATE_HEADERS, sampleRow]
    const ws = XLSX.utils.aoa_to_sheet(wsData)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Payouts')
    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' })
    const blob = new Blob([wbout], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'payouts-bulk-template.xlsx'
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
  }

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setResult(null)
    setError(null)
    const f = e.target.files?.[0]
    if (!f) return

    // Client-side validation: require .xlsx file
    const name = f.name || ''
    if (!name.toLowerCase().endsWith('.xlsx')) {
      setFile(null)
      setError('Only .xlsx Excel files are accepted. Please convert your file to .xlsx and try again.')
      return
    }

    setFile(f)
  }

  const handleUpload = async () => {
    if (!file) return setError('Please select a file')
    setUploading(true)
    setError(null)
    setResult(null)
    try {
      const res = await payoutService.bulkUpload(file)
      setResult(res)
    } catch (err: any) {
      console.error('Bulk upload failed', err)
      const msg = err?.response?.data?.message || err?.message || String(err)
      // Detect ExcelJS / JSZip parse error and show a user-friendly message
      if (String(msg).includes("Can't find end of central directory") || String(msg).toLowerCase().includes('is this a zip file')) {
        setError('The uploaded file could not be parsed as a valid .xlsx file. Please upload a valid Excel (.xlsx) file generated from the template.')
      } else {
        setError(msg)
      }
    } finally {
      setUploading(false)
    }
  }

  const downloadFailures = () => {
    if (!result?.failures || !Array.isArray(result.failures)) return
    const rows = result.failures.map((f: any) => `${f.row},"${String(f.riderPublicID || '').replace(/"/g, '""')}","${String(f.reason || '').replace(/"/g, '""')}"`)
    const csv = `row,riderPublicID,reason\n${rows.join('\n')}`
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'payouts-bulk-failures.csv'
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Bulk Payout Upload</Typography>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" onClick={() => navigate('/payouts')}>
            Back to Payouts
          </Button>
        </Stack>
      </Box>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6">Instructions</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Download the template, fill rows starting from the second row, and upload a valid Excel file in <strong>.xlsx</strong> format. For rows with <strong>status = SUCCESS</strong>, provide <strong>periodStartDate</strong> and <strong>periodEndDate</strong> in YYYY-MM-DD format. CSV uploads may not be accepted by the backend.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 2 }}>
            <Button variant="contained" startIcon={<CloudUploadIcon />} onClick={downloadTemplate}>
              Download Template
            </Button>
            <Button component="label" variant="outlined" startIcon={<CloudUploadIcon />}>
              Select File (only .xlsx)
              <input hidden type="file" accept=".xlsx" onChange={onFileChange} />
            </Button>
            <Button variant="contained" onClick={handleUpload} disabled={!file || uploading} startIcon={uploading ? <CircularProgress size={16} /> : null}>
              Upload
            </Button>
            {file && <Typography sx={{ alignSelf: 'center' }}>{file.name}</Typography>}
          </Stack>

          {error && (
            <Typography color="error" sx={{ mt: 2 }}>
              {error}
            </Typography>
          )}
        </CardContent>
      </Card>

      {result && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6">Upload Result</Typography>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item>
                <Paper sx={{ p: 2 }}>
                  <Typography>Total Rows</Typography>
                  <Typography variant="h5">{result?.summary?.totalRows ?? '-'}</Typography>
                </Paper>
              </Grid>
              <Grid item>
                <Paper sx={{ p: 2 }}>
                  <Typography>Success</Typography>
                  <Typography variant="h5">{result?.summary?.successCount ?? '-'}</Typography>
                </Paper>
              </Grid>
              <Grid item>
                <Paper sx={{ p: 2 }}>
                  <Typography>Failed</Typography>
                  <Typography variant="h5">{result?.summary?.failureCount ?? '-'}</Typography>
                </Paper>
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />

            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
              <Button variant="outlined" onClick={downloadFailures} disabled={!result?.failures?.length}>Download Failures</Button>
            </Stack>

            {result?.failures?.length > 0 && (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Row</TableCell>
                    <TableCell>Rider Public ID</TableCell>
                    <TableCell>Reason</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {result.failures.map((f: any, idx: number) => (
                    <TableRow key={idx}>
                      <TableCell>{f.row}</TableCell>
                      <TableCell>{f.riderPublicID}</TableCell>
                      <TableCell>{f.reason}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  )
}

export default PayoutsBulkUpload
